<template>
    <v-dialog v-model="dialog" max-width="600" persistent>
        <v-card>
            <v-card-title class="primary white--text title">
                {{ 'Generate API Token'|translate }}
            </v-card-title>
            <v-card-text>
                <div v-if="!generatedToken">
                    <v-text-field 
                        :label="'Username'|translate" 
                        v-model="username"
                        autofocus
                    ></v-text-field>
                    <v-text-field 
                        :label="'Password'|translate" 
                        v-model="password"
                        type="password"
                        @keyup.enter="generateToken"
                    ></v-text-field>
                    <v-text-field 
                        v-if="twoFactorAuth"
                        :label="'Two Factor Code'|translate" 
                        v-model="twoFactorInput"
                        @keyup.enter="generateToken"
                    ></v-text-field>
                    <div v-if="message" class="error--text mb-2">
                        {{ message }}
                    </div>
                </div>
                <div v-if="generatedToken">
                    <p class="mb-3">{{ 'Your API token has been generated. Copy and store it securely:'|translate }}</p>
                    <v-textarea 
                        :label="'API Token'|translate"
                        v-model="generatedToken"
                        readonly
                        :append-icon="'fa-copy'"
                        @click:append="copyToken"
                        auto-grow
                        rows="3"
                    ></v-textarea>
                    <v-alert type="info" :value="true" class="mb-0">
                        {{ 'This token has a long expiration and can be used for API authentication. Keep it secure and do not share it.'|translate }}
                    </v-alert>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn outline class="mr-2" @click="closeDialog">
                    {{ (generatedToken ? 'Close' : 'Cancel') | translate }}
                </v-btn>
                <v-btn 
                    v-if="!generatedToken"
                    class="primary mr-2 elevation-4" 
                    @click="generateToken"
                    :loading="loading"
                >
                    {{ 'Generate Token'|translate }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { login } from '../../helper'

export default {
    name: 'LoginTokenDialog',
    props: {
        value: { type: Boolean, default: false }
    },
    watch: {
        value() {
            if (this.dialog !== this.value) {
                this.dialog = this.value
            }
        },
        dialog() {
            this.$emit('input', this.dialog)
            if (this.dialog) {
                this.resetForm()
            }
        }
    },
    data() {
        return {
            dialog: false,
            username: '',
            password: '',
            twoFactorInput: '',
            twoFactorAuth: false,
            message: '',
            loading: false,
            generatedToken: ''
        }
    },
    methods: {
        async generateToken() {
            this.loading = true
            this.message = ''
            
            try {
                let res = await login(this.username, this.password, this.twoFactorInput, true) // long_expiration = true
                
                if (res.result === 'OK') {
                    this.generatedToken = res.token
                    this.message = ''
                } else if (res.result === '2fa') {
                    this.message = res.desc || this.$options.filters.translate('Two-factor authentication required')
                    this.twoFactorAuth = true
                } else {
                    this.message = res.desc || this.$options.filters.translate('Login failed. Please check your credentials.')
                }
            } catch (error) {
                this.message = this.$options.filters.translate('An error occurred while generating the token.')
                console.error('Token generation error:', error)
            } finally {
                this.loading = false
            }
        },
        
        copyToken() {
            if (this.generatedToken) {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(this.generatedToken).then(() => {
                        this.$store.dispatch('callSnackbar', { text: this.$options.filters.translate('Token copied to clipboard') })
                    }).catch(() => {
                        this.fallbackCopyToken()
                    })
                } else {
                    this.fallbackCopyToken()
                }
            }
        },
        
        fallbackCopyToken() {
            // Fallback for older browsers
            const textArea = document.createElement('textarea')
            textArea.value = this.generatedToken
            document.body.appendChild(textArea)
            textArea.select()
            document.execCommand('copy')
            document.body.removeChild(textArea)
            this.$store.dispatch('callSnackbar', { text: this.$options.filters.translate('Token copied to clipboard') })
        },
        
        closeDialog() {
            this.resetForm()
            this.dialog = false
        },
        
        resetForm() {
            this.username = ''
            this.password = ''
            this.twoFactorInput = ''
            this.twoFactorAuth = false
            this.message = ''
            this.loading = false
            this.generatedToken = ''
        }
    }
}
</script>

<style scoped>
</style>